import classNames from "classnames";
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ToastV2 from "../ToastV2/ToastV2";
import { FieldSchemaError } from "./useFieldSchema";
import { CoreFieldProps } from "../Fields/types/fieldTypes";
import { FieldSchemaFormBuilder } from "./types";
import { FieldConfig } from "./FieldConfig";
import { get, has } from "lodash";
import Field from "./Field";

export const FormDisabledContext = React.createContext<boolean | undefined>(
  false
);

export const FormFieldPropsContext = React.createContext<
  Partial<CoreFieldProps>[] | undefined
>(undefined);

export interface FieldFormProps<TModel> {
  /** Callback function called when the form is submitted. */
  onFormSubmit?: (value: TModel) => Promise<void>;
  // TODO Waiting for Dialog Implementation
  //shouldBlockNavigation?: boolean;
  /** Initial or State form value. */
  value?: TModel;
  /** Instance of the Field Schema Form Builder used to configure the form fields. */
  fieldSchemaFormBuilder?: FieldSchemaFormBuilder<TModel>; // optional, else you need to manage it on the fields like today
  /** Callback function called when a field value is changed. */
  onFieldChanged?: (value: TModel) => void;
  /** Object containing custom configuration for specific fields. */
  customFieldConfig?: FieldConfig;
  /** Callback function called when form validation fails. */
  onErrors?: (errors: { [key: string]: string[] }) => void;
  /** Boolean indicating whether the form is disabled or not. */
  isDisabled?: boolean;
  /** Indicates field positioning columns. Default 1. */
  columns?: number;
  children?: React.ReactNode;
}

export interface FieldFormModule<TModel> {
  /** Resets the form to its initial state. */
  reset: () => void;
  /** Submits the form. */
  submit: () => void;
  /** Returns an object containing form values. */
  getFormValue: () => TModel;
}

// Module augmentations allow you to extend the types of existing modules.
// https://www.digitalocean.com/community/tutorials/typescript-module-augmentation#enter-module-augmentation
declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

const FieldFormComponent = forwardRef(
  <TModel extends Object>(
    props: FieldFormProps<TModel>,
    ref: ForwardedRef<FieldFormModule<TModel>>
  ) => {
    useImperativeHandle(
      ref,
      () =>
        ({
          reset: () => {
            onValueChanged(resetValue.current);
          },
          getFormValue() {
            return formValue;
          },
          submit: () => {
            const form = formRef.current;
            if (form) {
              form.dispatchEvent(
                new Event("submit", { cancelable: true, bubbles: true })
              );
            }
          },
        }) as FieldFormModule<TModel>
    );

    const formRef = useRef<HTMLFormElement>(null);
    const resetValue = useRef<TModel>({} as TModel);

    const [isFormDisabled, setIsFormDisabled] = useState<boolean | undefined>(
      false
    );
    const [formValue, setFormValue] = useState<TModel>(
      props.value || ({} as TModel)
    );
    const [formFieldsData, setFormFieldsData] = useState<
      Partial<CoreFieldProps>[]
    >([]);

    useEffect(() => {
      setFormValue(props.value || ({} as TModel));
    }, [props.value]);

    useEffect(() => {
      resetValue.current = props.value || ({} as TModel);
    }, []);

    useEffect(() => {
      setIsFormDisabled(props.isDisabled);
    }, [props.isDisabled]);

    const [errors, _setErrors] = useState<{
      [key: string]: string[];
    }>({});

    useEffect(() => {
      updateFormFieldData();
    }, [formValue, errors]);

    const onValueChanged = (newValue: TModel) => {
      if (JSON.stringify(newValue) === JSON.stringify(formValue)) {
        return;
      }
      setFormValue({ ...newValue });
      props.onFieldChanged && props.onFieldChanged(newValue);
    };

    const updateFormFieldData = () => {
      if (!props.fieldSchemaFormBuilder) {
        return;
      }

      const fields = props.fieldSchemaFormBuilder(
        onValueChanged,
        formValue,
        errors,
        props.onFormSubmit
      );

      setFormFieldsData([...fields]);
    };

    return (
      <>
        {/* TODO Waiting for Dialog Implementation */}
        {/* {props.shouldBlockNavigation === true && (
        <SaveChangeDialog
          isDirty={
            JSON.stringify(props.value) !==
            JSON.stringify(props.formValueState.formValue)
          }
        />
      )} */}
        <form
          ref={formRef}
          className={classNames(
            `field-form${
              props.columns
                ? " field-form-grid field-form-grid-" + props.columns
                : ""
            }`,
            { "field-form-disabled": isFormDisabled }
          )}
          onSubmit={(e) => {
            e.preventDefault();

            props.onFormSubmit &&
              props
                .onFormSubmit(formValue)
                .then(() => {
                  // Keep value in sync with form since form reset value after submit.
                  setFormValue(formValue);
                  props.onErrors && props.onErrors({});
                })
                .catch((error) => {
                  if (error.response?.data?.errors) {
                    console.error(
                      "Runtime Crash Error",
                      error.response.data.errors
                    );

                    ToastV2.showFailedActionToaster(
                      "An error occurred executing the current action."
                    );
                  } else {
                    props.onErrors &&
                      ((error as FieldSchemaError)?.data
                        ? props.onErrors((error as FieldSchemaError).data)
                        : props.onErrors(error.response.data.additionalInfo));
                  }
                });
          }}
        >
          <fieldset>
            <FormDisabledContext.Provider value={isFormDisabled}>
              <FormFieldPropsContext.Provider value={formFieldsData}>
                {props.children && props.children}
                {!props.children && (
                  <>
                    {formFieldsData
                      .filter((x) => !x.hidden)
                      .map((field, idx) => {
                        const renderKey = has(field, "renderKey")
                          ? `${get(field, "renderKey")}idx`
                          : idx;

                        if (field.fieldName === undefined) {
                          throw new Error(
                            "Field name is required, but is undefined on the provided field schema."
                          );
                        }

                        return (
                          <div
                            className={classNames(
                              "field-form-field",
                              `field-form-field-${field.width || 100}`
                            )}
                            key={renderKey}
                          >
                            <Field
                              fieldProps={field.fieldName}
                              customFieldConfig={props.customFieldConfig}
                            />
                          </div>
                        );
                      })}
                  </>
                )}
              </FormFieldPropsContext.Provider>
            </FormDisabledContext.Provider>
          </fieldset>
        </form>
      </>
    );
  }
);

export const FieldForm = FieldFormComponent;
export default FieldForm;
